import { isDefined } from '@sgme/fp';
import type { ScheduleRunStatus } from 'apiConnector/scheduledRun/model';
import type { CreateRunRequest, UpdateRunRequest } from 'apiConnector/scheduledRun/scheduledRuns';
import { uniqBy } from 'lodash-es';
import { BlotterItemAction } from 'models/blotters/BlotterItemAction';
import type { FormError } from 'models/formError';
import { BatchFormInfos } from 'state/batchForm/infos/infos.model';
import { QuoteAction } from 'state/common-form/infos/infos.model';
import { SCHEDULED_FIELDS, type ScheduledRunFields } from 'state/scheduledRuns/state';
import type { AppState } from 'state/state.model';
import type { StrictOmit } from 'types/utils';
import { asSuccess } from 'utils/status';

type IdentifiedUpdateRunRequest = { request: UpdateRunRequest; etag: string; runId: string };

export function getStatusRequest(
  state: AppState,
  id: string,
  status: ScheduleRunStatus,
): IdentifiedUpdateRunRequest | undefined {
  const runToUpdate = asSuccess(state.scheduledRun.runs[id]?.runs)?.content.find(r => r.isSelected);
  if (!runToUpdate) {
    return;
  }
  return {
    request: { status },
    etag: runToUpdate.etag,
    runId: runToUpdate.id,
  };
}
export function getUpdateScheduleRequest(state: AppState, id: string): IdentifiedUpdateRunRequest | undefined {
  const runToUpdate = asSuccess(state.scheduledRun.runs[id]?.runs)?.content.find(r => r.isSelected);
  if (!runToUpdate) {
    return;
  }
  const run = getEditRequest(state, id);
  return {
    request: {
      daysTrigger: run?.daysTrigger,
      rangeTrigger: run?.rangeTrigger,
      readers: run?.readers,
      comment: state.scheduledRun.runs[id]?.comment,
    },
    etag: runToUpdate.etag,
    runId: runToUpdate.id,
  };
}

export function getAddToScheduleRequest(
  { scheduledRun: { runs } }: AppState,
  id: string,
): IdentifiedUpdateRunRequest | undefined {
  const run = asSuccess(runs[id]?.runs)?.content.find(r => r.isSelected);
  if (!isDefined(run)) {
    return undefined;
  }
  return {
    request: { quotes: uniqBy([...run.quotes, ...runs[id].quotes], q => q.quoteId), comment: run.comment },
    etag: run.etag,
    runId: run.id,
  };
}
export function getUpdateQuotesRequest(
  { scheduledRun: { runs } }: AppState,
  id: string,
): IdentifiedUpdateRunRequest | undefined {
  const run = asSuccess(runs[id]?.runs)?.content.find(r => r.isSelected);
  if (!isDefined(run)) {
    return undefined;
  }
  return {
    request: { quotes: uniqBy(runs[id].quotes, q => q.quoteId) },
    etag: run.etag,
    runId: run.id,
  };
}

export function getEditRequest(
  { scheduledRun: { runs, timeZone }, user: { icId, login } }: AppState,
  id: string,
): CreateRunRequest | undefined {
  const run = runs[id];
  if (!isDefined(run) || !isDefined(run.title) || run.quotes.length === 0 || !isDefined(run.time)) {
    return undefined;
  }
  const request: StrictOmit<CreateRunRequest, 'daysTrigger' | 'rangeTrigger'> = {
    name: run.title,
    quotes: run.quotes,
    comment: run.comment,
    readers:
      run.readers.length === 0 ? [{ icId, email: login }] : run.readers.map(r => ({ email: r.email, icId: r.icId })),
  };
  if (run.triggerMode === 'days') {
    return {
      ...request,
      daysTrigger: { days: run.days, time: run.time, timeZone },
    };
  } else if (isDefined(run.range) && isDefined(run.frequency.value)) {
    return {
      ...request,
      rangeTrigger: {
        startDate: run.range.startDate,
        endDate: run.range.endDate,
        frequency: { type: run.frequency.type, value: run.frequency.value },
        time: run.time,
        timeZone,
      },
    };
  }
}

const EMPTY_RANGE_ERROR: FormError<ScheduledRunFields> = {
  fields: ['range'],
  translationKey: 'scheduledRun.errors.emptyDateRange',
  isMandatory: true,
};
const EMPTY_FREQUENCY_ERROR: FormError<ScheduledRunFields> = {
  fields: ['frequency'],
  translationKey: 'scheduledRun.errors.emptyFrequency',
  isMandatory: true,
};
const EMPTY_DAYS_ERROR: FormError<ScheduledRunFields> = {
  fields: ['days'],
  translationKey: 'scheduledRun.errors.emptyDays',
  isMandatory: true,
};
const EMPTY_TITLE_ERROR: FormError<ScheduledRunFields> = {
  fields: ['title'],
  translationKey: 'scheduledRun.errors.emptyTitle',
  isMandatory: true,
};
const EMPTY_TIME_ERROR: FormError<ScheduledRunFields> = {
  fields: ['time'],
  translationKey: 'scheduledRun.errors.emptyTime',
  isMandatory: true,
};
export function getScheduledFieldsErrors(
  { scheduledRun: { runs } }: AppState,
  id: string,
): readonly FormError<ScheduledRunFields>[] {
  const run = runs[id];
  let errors: FormError<ScheduledRunFields>[] = [];

  if (!run) {
    return [];
  }
  if (run.triggerMode === 'range') {
    if (!run.range) {
      errors = [...errors, EMPTY_RANGE_ERROR];
    }
    if (!run.frequency.value) {
      errors = [...errors, EMPTY_FREQUENCY_ERROR];
    }
  }
  if (run.triggerMode === 'days' && run.days.length === 0) {
    errors = [...errors, EMPTY_DAYS_ERROR];
  }
  if (!run.title) {
    errors = [...errors, EMPTY_TITLE_ERROR];
  }
  if (!run.time) {
    errors = [...errors, EMPTY_TIME_ERROR];
  }
  if (run.existingTitle && run.title === run.existingTitle) {
    errors = [
      ...errors,
      {
        fields: ['title'],
        severity: 'Warning',
        translationKey: 'scheduledRun.errors.titleAlreadyExists',
        values: { title: run.existingTitle },
      },
    ];
  }

  return errors.map(e => ({ ...e, isHidden: e.fields?.every(f => run.pristineFields.includes(f)) }));
}

export function hasSelectedRun({ scheduledRun: { runs } }: AppState, id: string): boolean {
  return asSuccess(runs[id].runs)?.content.some(r => r.isSelected) ?? false;
}

const scheduledFieldsLength = SCHEDULED_FIELDS.length;
export function hasChanged({ scheduledRun: { runs } }: AppState, id: string): boolean {
  const run = runs[id];
  return run !== undefined && runs[id].pristineFields.length !== scheduledFieldsLength;
}

export function getStaticActiveTabActions(state: AppState): ReadonlyArray<QuoteAction> {
  const staticActiveTabId = state.workspaces.workspaces.static.activeTab;
  return staticActiveTabId ? state.static.infos[staticActiveTabId].actions : [];
}
export function getDynamicActiveTabActions(state: AppState): ReadonlyArray<QuoteAction> {
  const dynamicActiveTabId = state.workspaces.workspaces.dynamic.activeTab;
  return dynamicActiveTabId ? state.dynamic.infos[dynamicActiveTabId].actions : [];
}
export function getBatchSelectedQuotes(state: AppState): ReadonlyArray<BatchFormInfos> {
  const quotes = Object.values(state.batchForm.infos).map(info => info);
  return quotes.filter(quote => quote.isSelected);
}

export function getBlottersQuoteActions(state: AppState): ReadonlyArray<BlotterItemAction> {
  return state.blotters.blotterPanel.actions ?? [];
}
